import { useSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Grid, Alert, Stack, Avatar, Button, Dialog, Checkbox, TextField, Typography, DialogTitle, Autocomplete, DialogContent, FormControlLabel } from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { IJob, VisibilityRule } from 'src/services/jobs/jobs.types';
import { DocumentType } from 'src/services/files/file.types';
import { isShadowEmail } from 'src/services/candidates/candidates.utils';
import { generateAddCandidateToJobError } from 'src/services/jobs/jobs.utils';
import { ApplicationStage } from 'src/services/applications/applications.types';
import {
  useGetProfileDocumentsQuery,
  useGetCandidateDocumentsQuery,
  useGetOrganizationCandidateQuery,
} from 'src/services/candidates/candidates.service';
import { useGetOrganisationJobsQuery, useAddCandidateToJobMutation } from 'src/services/jobs/jobs.service';

import Label from 'src/components/label';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  candidateId: string;
};

const ALLOWED_STAGES = [
  ApplicationStage.APPLIED,
  ApplicationStage.SCREENING
];

export default function AddCandidateJobModal({ candidateId, open, onClose }: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const tenantType = useOrgTenant();

  const router = useRouter();

  const { canAccess } = useFeatures();

  const [error, setError] = useState<string | null>(null);

  const [jobSearchQuery, setJobSearchQuery] = useState<string>('');

  const [defaultStage, setDefaultStage] = useState<ApplicationStage>(ApplicationStage.APPLIED);

  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [createInvite, setCreateInvite] = useState<boolean>(true);

  const { currentData: jobs } = useGetOrganisationJobsQuery({
    search_query: jobSearchQuery,
    page: 1,
    per_page: 5
  });

  const { currentData: orgCandidate, isLoading: isCandidateLoading } = useGetOrganizationCandidateQuery(candidateId, {
    skip: !candidateId
  });

  const [addCandidateToJob, { isLoading: isSubmitting }] = useAddCandidateToJobMutation();

  const profileId = orgCandidate?.candidate_profile?.id;

  // A candidate's attachable documents live in two stores (recruiter uploads + PUBLIC profile docs);
  // list the union so the recruiter picks which transfer to (and get shared from) the application, rather
  // than cloning them all (JT1-1524 follow-up). Non-PUBLIC profile docs are never cloneable, so omitted.
  const { currentData: orgDocs } = useGetCandidateDocumentsQuery(
    { candidate_id: candidateId, params: { page: 1, per_page: 50 } },
    { skip: !candidateId || !open }
  );
  const { currentData: profileDocs } = useGetProfileDocumentsQuery(
    { profileId: profileId as string, params: { page: 1, per_page: 50 } },
    { skip: !profileId || !open }
  );

  const documents = useMemo(() => {
    const byDocId = new Map<string, { id: string; name: string; type?: DocumentType; createdAt?: string | Date }>();
    (orgDocs?.results ?? []).forEach((item) => {
      const doc = item.document;
      if (doc?.id && !byDocId.has(doc.id)) {
        byDocId.set(doc.id, { id: doc.id, name: doc.name ?? doc.filename, type: doc.type, createdAt: doc.created_at ?? item.created_at });
      }
    });
    (profileDocs?.results ?? []).forEach((item) => {
      const doc = item.document;
      const isPublic = item.visibility_rule === undefined || item.visibility_rule === VisibilityRule.PUBLIC;
      if (doc?.id && isPublic && !byDocId.has(doc.id)) {
        byDocId.set(doc.id, { id: doc.id, name: doc.name ?? doc.filename, type: doc.type, createdAt: doc.created_at });
      }
    });
    return Array.from(byDocId.values());
  }, [orgDocs, profileDocs]);

  // Default selection = the newest resume only; the recruiter adjusts before creating the application.
  const defaultDocIds = useMemo(() => {
    const resumes = documents.filter((doc) => doc.type === DocumentType.RESUME);
    const newest = [...resumes].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    )[0];
    return newest ? [newest.id] : [];
  }, [documents]);

  // null = follow the default; becomes a Set once the recruiter toggles anything.
  const [docOverride, setDocOverride] = useState<Set<string> | null>(null);
  const selectedDocs = useMemo(() => docOverride ?? new Set(defaultDocIds), [docOverride, defaultDocIds]);
  const toggleDoc = useCallback((id: string) => {
    setDocOverride((prev) => {
      const next = new Set(prev ?? defaultDocIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [defaultDocIds]);

  // Reset the picker when the dialog closes or the candidate changes.
  useEffect(() => { if (!open) setDocOverride(null); }, [open]);
  useEffect(() => { setDocOverride(null); }, [candidateId]);

  const canAccessInviteFeature = useMemo(() => canAccess(FeatureType.JOB_INVITES_ENABLED), [canAccess]);

  const hasRealEmail = !isShadowEmail(orgCandidate?.email);

  const onCloseDialog = useCallback(() => {

    setSelectedJob(null);
    onClose();
  }, [onClose]);

  const handleInviteCandidate = useCallback(async () => {
    try {

      if (!selectedJob || !orgCandidate) return;

      await addCandidateToJob({
        job_id: selectedJob.id as string,
        candidate_id: orgCandidate.id as string,
        create_invite: createInvite && !isShadowEmail(orgCandidate.email),
        default_stage: defaultStage,
        // Only send a selection when documents actually loaded; otherwise fall back to attach-all so a
        // slow/failed documents fetch never silently strips the resume (JT1-1524 follow-up).
        document_ids: documents.length ? Array.from(selectedDocs) : undefined,
      }).unwrap();

      enqueueSnackbar(t('candidates.invites.add_to_job_modal.api.success'), { variant: 'success' });

      setSelectedJob(null);
      onCloseDialog();
    } catch (e) {
      setError(generateAddCandidateToJobError(e));
      enqueueSnackbar(generateAddCandidateToJobError(e), { variant: 'error' });
    }
  }, [addCandidateToJob, createInvite, defaultStage, documents, selectedDocs, enqueueSnackbar, onCloseDialog, orgCandidate, selectedJob, t]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseDialog}>
      <DialogTitle>{t(`candidates.invites.add_to_job_modal.title`)}</DialogTitle>
      <DialogContent>
        {!!error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2} justifyContent="center" pt={1}>
          <Grid item xs={12}>
            <Alert variant="outlined" severity="info">
              {t(`candidates.invites.add_to_job_modal.alert`)}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center" my={3} px={1}>
              <Avatar variant="rounded"
                src={orgCandidate?.candidate_profile.profile_image?.public_path as string}
                alt={orgCandidate?.candidate_profile.first_name}
              />
              <Stack direction="column">
                <Typography variant="h6">
                  {orgCandidate?.candidate_profile.first_name} {orgCandidate?.candidate_profile.last_name}
                </Typography>
                <Typography variant="caption">
                  {orgCandidate?.candidate_profile.position_title}
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} >
            <Autocomplete
              options={jobs?.results as IJob[] ?? []}
              value={selectedJob}
              disabled={!canAccessInviteFeature}
              onInputChange={(_event, value) => setJobSearchQuery(value)}
              onChange={(_event, value) => setSelectedJob(value)}
              renderOption={({ key, ...props }, option: IJob) => (
                <li key={key} {...props}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.title}`}
                    </Typography>

                    {
                      tenantType === TenantType.Recruiter && (
                        <Typography variant="caption">{option.client_company_name}</Typography>
                      )
                    }

                  </Stack>
                </li>
              )}
              getOptionLabel={(option) =>
                option ? `${option.title}` : ''
              }
              isOptionEqualToValue={(option: IJob, value: IJob) => option.id === value?.id}
              getOptionKey={(option) => (option as IJob).id as string}
              renderInput={(params) => (
                <TextField {...params} label={t('jobs.toolbar.search.placeholder')} />
              )}
              sx={{
                minWidth: 200,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              options={ALLOWED_STAGES}
              value={defaultStage}
              onChange={(_event, value) => setDefaultStage(value as ApplicationStage)}
              renderOption={({ key, ...props }, option: ApplicationStage) => (
                <li key={key} {...props}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${t(`enums.application_stage.${option}`)}`}
                    </Typography>
                  </Stack>
                </li>
              )}
              getOptionLabel={(option) =>
                option ? `${t(`enums.application_stage.${option}`)}` : ''
              }
              isOptionEqualToValue={(option: ApplicationStage, value: ApplicationStage) => option === value}
              getOptionKey={(option) => (option as ApplicationStage)}
              renderInput={(params) => (
                <TextField {...params} label={t('candidates.invites.add_to_job_modal.default_stage.label')} />
              )}
              sx={{
                minWidth: 200,
              }}
            />
          </Grid>

          {documents.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ px: 1 }}>
                {t('candidates.invites.add_to_job_modal.documents.label', { defaultValue: 'Documents to include' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block' }}>
                {t('candidates.invites.add_to_job_modal.documents.subtitle', {
                  defaultValue: 'Only the documents you select are attached to the application and shared with the client.',
                })}
              </Typography>
              <Stack sx={{ px: 1, mt: 1 }}>
                {documents.map((doc) => (
                  <FormControlLabel
                    key={doc.id}
                    control={
                      <Checkbox
                        checked={selectedDocs.has(doc.id)}
                        onChange={() => toggleDoc(doc.id)}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{doc.name}</Typography>
                        {doc.type === DocumentType.RESUME && (
                          <Label variant="soft" color="primary">
                            {t('common.resume', { defaultValue: 'Resume' })}
                          </Label>
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Stack>
            </Grid>
          )}

          <Grid item xs={12} mb={5} >
            <FormControlLabel
              sx={{ pl: 1, alignItems: 'start' }}
              labelPlacement='end'
              disabled={!hasRealEmail}
              label={
                <Stack direction="column">
                  <Typography variant="subtitle2">{t('candidates.invites.add_to_job_modal.create_invite.label')}</Typography>
                  <Typography variant="caption">{t('candidates.invites.add_to_job_modal.create_invite.subtitle')}</Typography>
                </Stack>
              }
              control={
                <Checkbox
                  checked={createInvite && hasRealEmail}
                  onChange={(e) => setCreateInvite(e.target.checked)}
                  color="primary"
                />
              }
            />
          </Grid>

          <Stack sx={{ py: 2, width: '100%' }} gap={1} flexDirection="row" justifyContent="flex-end" alignItems="center">

            <Button variant='text' onClick={onCloseDialog}>{t('common.cancel')}</Button>

            {
              canAccessInviteFeature ? (
                <LoadingButton
                  type="submit"
                  variant="contained"
                  onClick={handleInviteCandidate}
                  disabled={isSubmitting || !selectedJob || !orgCandidate}
                  loading={isSubmitting}
                >
                  {t('candidates.add_to_job')}
                </LoadingButton>
              ) : (
                <FeatureLockedChip text={t('common.upgrade')} />
              )
            }

          </Stack>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
